import { ResolvedProjectMetadata } from "../projects/project-metadata-options";

/**
 * Default fallback placeholders for unresolved template variables.
 */
const FALLBACKS: Record<string, string> = {
  "repository.owner": "<owner>",
  "repository.name": "<repo>",
  "repository.defaultBranch": "main",
  "organization.name": "<organization>",
  "organization.githubOrg": "<org>",
  "githubProject.name": "<project-name>",
  "githubProject.number": "<project-number>",
  "githubProject.nodeId": "<project-node-id>",
  docsPath: "<docs-path>",
  // The monorepo-layout seed block is additive: when absent, the
  // seeded `project-context.md` template reads cleanly without it.
  // Fall back to an empty string so no placeholder text leaks into
  // rendered prompts for repos that predate the layout contract.
  monorepoLayoutSeedBlock: "",
};

/** Matches `{{dotted.path}}` template variables. */
const TEMPLATE_RE = /\{\{(\w+(?:\.\w+)*)\}\}/g;

/**
 * Looks up a dotted path (e.g., "repository.owner") on a metadata object.
 * Returns the value as a string, or undefined if not found.
 */
function getNestedValue(
  obj: Record<string, unknown>,
  path: string,
): string | undefined {
  const parts = path.split(".");
  let current: unknown = obj;

  for (const part of parts) {
    if (current == null || typeof current !== "object") {
      return undefined;
    }
    current = (current as Record<string, unknown>)[part];
  }

  if (current == null) {
    return undefined;
  }

  return String(current);
}

/**
 * Result of template variable resolution.
 */
export interface TemplateResolveResult {
  /** The template string with all variables resolved or replaced with fallbacks. */
  readonly resolved: string;
  /** Template variable keys that could not be resolved from metadata. */
  readonly unresolvedKeys: ReadonlyArray<string>;
}

/**
 * Resolves `{{dotted.path}}` template variables in a string using project metadata.
 * Unresolved variables are replaced with their fallback placeholders.
 *
 * @param template - String potentially containing `{{variable}}` patterns
 * @param metadata - Resolved project metadata, or undefined if not available
 * @returns The resolved string and a list of any unresolved variable keys
 */
export function resolveTemplateVariables(
  template: string,
  metadata: ResolvedProjectMetadata | undefined,
): TemplateResolveResult {
  if (!TEMPLATE_RE.test(template)) {
    return { resolved: template, unresolvedKeys: [] };
  }

  const unresolvedKeys: string[] = [];

  // Reset regex lastIndex since we used .test() above
  TEMPLATE_RE.lastIndex = 0;

  const resolved = template.replace(TEMPLATE_RE, (_match, key: string) => {
    if (metadata) {
      const value = getNestedValue(
        metadata as unknown as Record<string, unknown>,
        key,
      );
      if (value !== undefined) {
        return value;
      }
    }

    unresolvedKeys.push(key);
    return FALLBACKS[key] ?? `<${key}>`;
  });

  return { resolved, unresolvedKeys };
}
